import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from 'react-query';

import Button from '../general/Button';
import { apiQueryKey } from '../../api/useApi';
import {
    useEscalateSupportConversation,
    useMarkSupportConversationRead,
    useRateSupportMessage,
    useSendSupportMessage,
    useStartSupportConversation,
    useSupportConversation,
    useSupportConversations,
} from '../../api/support/useSupport';
import SupportChatEscalation from './SupportChatEscalation';
import SupportMessageItem from './SupportMessageItem';

interface SupportChatPanelProps {
    onClose: () => void;
}

export default function SupportChatPanel({ onClose }: SupportChatPanelProps) {
    const { t } = useTranslation('support');
    const { pathname } = useLocation();
    const [conversationId, setConversationId] = useState<number | null>(null);
    const [draft, setDraft] = useState('');
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const endRef = useRef<HTMLDivElement>(null);

    const { data: conversations } = useSupportConversations(conversationId === null);
    const { data: conversation } = useSupportConversation(conversationId);
    const start = useStartSupportConversation();
    const send = useSendSupportMessage();
    const escalate = useEscalateSupportConversation();
    const markRead = useMarkSupportConversationRead();
    const rate = useRateSupportMessage();

    const messages = conversation?.messages ?? [];
    const isPending = !!conversation?.assistant_pending || start.isLoading || send.isLoading;
    const canEscalate = conversation?.status === 'ai';

    // Re-marqué lu quand de nouveaux messages arrivent alors que le panneau est ouvert.
    // Le marquage invalide la conversation, mais le compte de messages ne change pas : pas de boucle.
    useEffect(() => {
        if (conversationId !== null) markRead.mutate({ conversationId });
    }, [conversationId, messages.length]);

    useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'end' }); }, [messages.length, isPending]);

    const handleSubmit = () => {
        const message = draft.trim();
        if (!message || isPending) return;
        setError(null);
        const onError = (err: unknown) => {
            setError(axios.isAxiosError(err) && err.response?.status === 409 ? t('error_busy') : t('error_generic'));
        };
        if (conversationId === null) {
            start.mutate({ message, page_path: pathname }, {
                onSuccess: (created) => {
                    queryClient.setQueryData(apiQueryKey(`/support/conversations/${created.id}`), created);
                    setConversationId(created.id);
                    setDraft('');
                }, onError,
            });
        } else {
            send.mutate({ conversationId, message, page_path: pathname }, { onSuccess: () => setDraft(''), onError });
        }
    };

    const handleFailure = () => setError(t('error_generic'));

    const handleEscalate = () => {
        if (conversationId === null || escalate.isLoading) return;
        escalate.mutate({ conversationId }, { onError: handleFailure });
    };

    useEffect(() => {
        const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); handleSubmit(); }
    };

    return (
        <div role="dialog" aria-label={t('title')}
            className="fixed bottom-24 right-4 z-50 flex h-[32rem] max-h-[75vh] w-[22rem] max-w-[calc(100vw-2rem)] flex-col rounded-2xl bg-white shadow-xl ring-1 ring-border-color-light">
            <header className="flex items-center justify-between rounded-t-2xl bg-primary-color px-4 py-3 text-white">
                <div className="flex items-center gap-2">
                    <h2 className="text-sm font-medium">{t('title')}</h2>
                    {conversation?.channel === 'email' && conversationId !== null && (
                        <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{t('by_email')}</span>
                    )}
                </div>
                <div className="flex items-center gap-3 text-xs">
                    <Link to="/aide" className="underline hover:no-underline">{t('help_center')}</Link>
                    {conversationId !== null && (
                        <Button type="primary-light" size="sm" elementType="button"
                            attributes={{ onClick: () => { setConversationId(null); setError(null); } }} text={t('new_question')} />
                    )}
                    <Button type="default-light" size="sm" elementType="button"
                        attributes={{ onClick: onClose, 'aria-label': t('close') }}>
                        <XMarkIcon className="h-5 w-5 text-white" />
                    </Button>
                </div>
            </header>

            <div className="flex-1 space-y-3 overflow-y-auto p-3">
                {conversationId === null ? (
                    (conversations ?? []).length === 0 ? (
                        <p className="text-sm text-text-color-light">{t('empty_state')}</p>
                    ) : (
                        <>
                            <p className="text-xs font-medium text-text-color-light">{t('recent')}</p>
                            {(conversations ?? []).map((c) => (
                                <button key={c.id} type="button" onClick={() => setConversationId(c.id)}
                                    className="flex w-full items-center justify-between rounded-md border border-main-color-light px-3 py-2 text-left text-sm hover:bg-main-color-light">
                                    <span className="truncate">{c.subject ?? `#${c.id}`}</span>
                                    {c.channel === 'email' && <span className="ml-2 shrink-0 rounded-full bg-main-color-light px-1.5 text-xs text-main-color-dark">{t('by_email')}</span>}
                                    {c.unread_count > 0 && <span className="ml-2 rounded-full bg-primary-color px-1.5 text-xs text-white">{c.unread_count}</span>}
                                </button>
                            ))}
                        </>
                    )
                ) : (
                    messages.map((m, index) => (
                        <SupportMessageItem key={m.id} message={m} showSubject={index === 0} canEscalate={canEscalate}
                            onRate={(messageId, rating) => rate.mutate({ messageId, rating }, { onError: handleFailure })}
                            onEscalate={handleEscalate} />
                    ))
                )}
                {isPending && <p className="text-sm italic text-text-color-light">{t('thinking')}</p>}
                <div ref={endRef} />
            </div>

            {conversationId !== null && (
                <SupportChatEscalation conversation={conversation} isBusy={isPending} onEscalate={handleEscalate} />
            )}
            {error && <p role="alert" className="px-3 py-1 text-xs text-alert-text-color">{error}</p>}

            <div className="flex items-end gap-2 border-t border-main-color-light p-3">
                <textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={handleKeyDown}
                    disabled={isPending} placeholder={t('placeholder')} aria-label={t('placeholder')}
                    className="flex-1 resize-none rounded-md border border-border-color-light p-2 text-sm focus:border-primary-color focus:outline-none disabled:opacity-50" />
                <Button type="primary" elementType="button" disabled={isPending || !draft.trim()}
                    attributes={{ onClick: handleSubmit }} text={t('send')} />
            </div>
        </div>
    );
}
